import { useRef, useState } from "react";
import { CalendarCheck, CalendarClock, Hospital, Moon, Pill, Sun, Sunrise, TriangleAlert } from "lucide-react";
import ProductLayout from "./sai/ProductLayout";
import StepTabs from "./sai/StepTabs";
import { IDS as SAI_IDS } from "./sai/ids";
import usePulse from "./sai/usePulse";
import { IDS } from "./hospitalIds";
import "./HospitalReports.css";

// Hospital reports, ported from sai's HospitalScreen (brochure-demo @ 2a7c405)
// into our shell: ProductLayout renders our <Screen> via SaiProductFrame; IDs
// are data-trapezium-* via toTrapezium; "Next product" follows our STEPS. His
// visitor session is not read: the prescription's letterhead is his own
// fallback, "Your Hospital".
//
// His notes: what the patient takes home. Two documents: a real discharge
// summary (identities removed) with a spotlight on the part each step is
// about, and the Smart Rx prescription, a design proposal (no live sample
// yet). Content: the founder's 19-hospital-reports.html.
//
// Points are headings only (their sub-lines went 2026-10-24).
//
// Phase 3 (Harsh, 2026-10-17): the spotlight on "what the patient sees" pulses
// twice on arrival and on every step change, then rests (none under reduced
// motion). Second fixes list: sai's own document cards are back (Discharge
// summary first, aria-pressed), and the prescription shows a realistic printed
// "before" beside the Smart Rx "after" in our UI.

const SCREEN = "hospital"; // his screen key (our product.hospital-reports)

const DOCS = {
  discharge: {
    name: "Discharge summary", foot: "Real sample, identities removed",
    steps: [
      { key: "problem", nav: "The problem", img: "ds1", spot: [2, 40, 96, 28], head: "One visit. Four documents that never met.",
        lede: "Lab, ward, consultant and radiology each send their own.",
        points: [["The stay in one panel"], ["Diagnosis up front"], ["Whitelabelled to you"]] },
      { key: "story", nav: "One story", img: "ds2", spot: [3, 36, 94, 16], head: "So one paragraph ties the folder together.",
        lede: "What was found, what was done, how the patient responded.",
        points: [["The hospital course"], ["Condition at discharge"], ["Everything points here"]] },
      { key: "medicines", nav: "Medicines", img: "ds3", spot: [2, 18.5, 96, 37], head: "Medicines that follow the diagnosis.",
        lede: "Each medicine traced to the diagnosis, with plain timing.",
        points: [["Dose and frequency"], ["Timing in plain words"], ["The risky ones flagged"]] },
      { key: "follow-up", nav: "Follow-up", img: "ds3", spot: [2, 55.5, 96, 14.5], head: "Ending in OPD, not in casualty.",
        lede: "A named department and a date: a planned visit, not a readmission.",
        points: [["Named follow-up"], ["OPD, not emergency"], ["Bookable"]] },
    ],
  },
  // One step only (2026-10-24: the "Take-home" and "Warnings" steps went).
  rx: {
    name: "Prescription", foot: "Smart Rx · design proposal",
    steps: [
      { key: "purpose", nav: "Purpose", spot: [2.5, 17.6, 95, 61.2], head: "Every medicine says what it is for.",
        lede: "“Antibiotic, clears the infection found on endoscopy” beats a drug name nobody can pronounce.",
        points: [["A purpose per medicine"], ["A schedule, not a sentence"], ["Duration stated"]] },
    ],
  },
};

// The Smart Rx medicines. `times`: when in the day (morning, afternoon, night);
// `chip`: a timing word in place of times (the weekly one); `tone`: the icon tile.
const MEDS = [
  { name: "Cap. Mox 500 mg", what: "Antibiotic: clears the infection found on endoscopy", times: ["morning", "night"], how: "After meals", long: "14 days", tone: "blue" },
  { name: "Cap. Vonogress 20 mg", what: "Reduces stomach acid", times: ["morning", "night"], how: "Before breakfast and dinner", long: "1 month", tone: "teal" },
  { name: "Tab. Budez CR 3 mg", what: "Calms intestinal inflammation", times: ["morning", "afternoon", "night"], how: "After meals", long: "As advised", tone: "purple" },
  { name: "Tab. Methotrexate 20 mg", what: "Once a week, on Sunday only, not daily", chip: "Sunday only", long: "Weekly", tone: "amber", flag: true },
  { name: "Tab. Folic acid", what: "Protects against methotrexate’s side effects", times: ["morning"], long: "Daily", tone: "green" },
];

const TIMES = {
  morning: { label: "Morning", Icon: Sunrise },
  afternoon: { label: "Afternoon", Icon: Sun },
  night: { label: "Night", Icon: Moon },
};

const PATIENT = "Mr. A. S."; // invented initials (synthetic)

// "After": the Smart Rx, in our UI (white rounded cards, tinted icon tiles,
// plain timing words). Readable text, so the step's spotlight has words under it.
function Rx({ org }) {
  return (
    <div className="hp-rx">
      <div className="hp-rx-lh">
        <span className="hp-rx-lg" aria-hidden="true"><Hospital size="1.3em" strokeWidth={2} /></span>
        <span className="hp-rx-org"><b>{org}</b><span>Prescription · OPD · Gastroenterology</span></span>
        <span className="hp-rx-badge">Smart Rx</span>
      </div>
      <div className="hp-rx-pt">
        <span>Patient<b>{PATIENT}</b></span><span>Age / Sex<b>42 / M</b></span><span>Review<b>After 1 week</b></span>
      </div>
      <p className="hp-rx-h">Your medicines <span>{MEDS.length} medicines</span></p>
      <ul className="hp-rx-meds">
        {MEDS.map((m) => (
          <li className={`hp-rx-med${m.flag ? " is-flag" : ""}`} key={m.name}>
            <span className={`hp-rx-ic is-${m.tone}`} aria-hidden="true"><Pill size="1.15em" strokeWidth={2.2} /></span>
            <span className="hp-rx-name"><b>{m.name}</b><span>{m.what}</span></span>
            <span className="hp-rx-dur">{m.long}</span>
            <span className="hp-rx-when">
              {m.chip ? (
                <span className="hp-rx-chip is-weekly"><CalendarClock size="1.1em" aria-hidden="true" />{m.chip}</span>
              ) : m.times.map((t) => {
                const { label, Icon } = TIMES[t];
                return <span className={`hp-rx-chip is-${t}`} key={t}><Icon size="1.1em" aria-hidden="true" />{label}</span>;
              })}
              {m.how ? <span className="hp-rx-how">{m.how}</span> : null}
            </span>
          </li>
        ))}
      </ul>
      <div className="hp-rx-alert">
        <span className="hp-rx-alert-ic" aria-hidden="true"><TriangleAlert size="1.2em" /></span>
        <span><b>Important: one thing to get right</b>Methotrexate is a weekly tablet, taken on Sunday. Taking it daily is the most common and most serious mistake with this prescription. Folic acid is the daily one.</span>
      </div>
      <div className="hp-rx-ft">
        <span className="hp-rx-ft-ic" aria-hidden="true"><CalendarCheck size="1.2em" /></span>
        <span>Next review<b>After 1 week, in OPD</b></span>
        <span className="hp-rx-bk">Book the review</span>
      </div>
    </div>
  );
}

// "Before": what the hospital actually hands over, a printed OPD slip. One
// named image (its words are shorthand on purpose). Synthetic: "Your Hospital",
// a made-up address and registration number, invented initials.
const PRINTED = [
  ["1.", "Cap. MOX 500mg", "1-0-1", "x 14d", "A/F"],
  ["2.", "Cap. VONOGRESS 20mg", "1-0-1", "x 1 mth", "B/F"],
  ["3.", "Tab. BUDEZ CR 3mg", "1-1-1", "", "A/F"],
  ["4.", "Tab. METHOTREXATE 20mg", "1 tab", "wkly (SUN)", ""],
  ["5.", "Tab. FOLIC ACID 5mg", "1-0-0", "OD", ""],
];

function PrintedRx({ org }) {
  return (
    <div className="hp-print" role="img" aria-label={`Before: the hospital’s printed prescription. A dense ${org} letterhead, medicines in shorthand such as 1-0-1 and A/F, methotrexate’s weekly dose written as “1 tab wkly (SUN)”, a stamp and a signature.`}>
      <div aria-hidden="true">
        <div className="hp-print-lh">
          <span className="hp-print-logo"><span>+</span></span>
          <span className="hp-print-org">
            <b>{org.toUpperCase()}</b>
            <i>Multi-speciality Hospital &amp; Research Centre</i>
            <span>Plot 00, Sample Road, Sector 0, Your City 000 000</span>
            <span>Ph: 000-0000 0000 · OPD 9 am to 5 pm · NABH Reg. 0000</span>
          </span>
        </div>
        <div className="hp-print-dept"><span>Dept. of Gastroenterology</span><span>OPD No. 26/000000</span></div>
        <div className="hp-print-pt">
          <span>Name: <u>{PATIENT}</u></span><span>Age/Sex: <u>42/M</u></span><span>Date: <u>__/__/2026</u></span>
        </div>
        <p className="hp-print-dx"><b>C/O:</b> epigastric pain, loose stools x 3 wks. <b>Dx:</b> Antral gastritis (H. pylori +ve); ?IBD</p>
        <p className="hp-print-rx">&#8478;</p>
        <ol className="hp-print-meds">
          {PRINTED.map(([n, d, x, y, z]) => (
            <li key={n}><span>{n}</span><span>{d}</span><span>{x}</span><span>{y}</span><span>{z}</span></li>
          ))}
        </ol>
        <p className="hp-print-adv"><b>Adv:</b> Soft bland diet. Avoid NSAIDs, spicy food. CBC, LFT before next visit. Cont. other meds as advised.</p>
        <p className="hp-print-hand">Review after 1 wk with reports</p>
        <div className="hp-print-ft">
          <span className="hp-print-stamp"><span>{org.toUpperCase()}</span><b>OPD</b><span>Reg. No. 00000</span></span>
          <span className="hp-print-sign">
            <svg viewBox="0 0 120 34" focusable="false"><path d="M4 24c8-14 14-18 16-12s-6 14-2 12 10-16 14-14-4 12 0 12 8-10 12-10 2 8 6 8 10-12 16-10-2 8 4 8 14-8 20-10 10 2 18-2" /></svg>
            <span>Consultant Gastroenterologist</span>
          </span>
        </div>
        <p className="hp-print-foot">This prescription is valid for 30 days · Not for medico-legal purposes</p>
      </div>
    </div>
  );
}

const ORG = "Your Hospital"; // his readSession() fallback; no visitor session here

// sai's document cards, in his order: the discharge summary first.
const DOC_ORDER = ["discharge", "rx"];

export default function HospitalReports() {
  const org = ORG;
  const [doc, setDoc] = useState("discharge");
  const [active, setActive] = useState(0);
  const cards = useRef({});
  const d = DOCS[doc];
  const step = d.steps[active];
  const [x, y, w, h] = step.spot;

  // The spotlight's pulse: on arrival, and again on every step or document change.
  const pulse = usePulse(`${doc}-${active}`, { onMount: true, ms: 2000 });

  const pickDoc = (k) => { if (k !== doc) { setDoc(k); setActive(0); } };
  // Both cards are in the Tab order (Enter / Space press them); the arrow keys,
  // Home and End also move between them, pressing the one they land on.
  const onDocKey = (e) => {
    const i = DOC_ORDER.indexOf(doc);
    const n = DOC_ORDER.length;
    const to = { ArrowRight: i + 1, ArrowDown: i + 1, ArrowLeft: i - 1, ArrowUp: i - 1, Home: 0, End: n - 1 }[e.key];
    if (to === undefined) return;
    e.preventDefault();
    const k = DOC_ORDER[(to + n) % n];
    pickDoc(k);
    cards.current[k]?.focus();
  };

  const spot = (
    <span className={`hp-spot${pulse ? " is-pulsing" : ""}`} aria-hidden="true" style={{ left: `${x}%`, top: `${y}%`, width: `${w}%`, height: `${h}%` }}>
      {pulse ? <span key={pulse} className="hp-ring" /> : null}
    </span>
  );

  const visual = (
    <div className="hp" data-trapezium-view={doc}>
      {doc === "rx" ? (
        <div className="hp-pair">
          <figure className="hp-sheet hp-sheet--before">
            <figcaption className="hp-tag"><span>Before</span><span>What the hospital wrote</span></figcaption>
            <PrintedRx org={org} />
          </figure>
          <figure className="hp-sheet hp-sheet--after">
            <figcaption className="hp-tag hp-tag--after"><span>After</span><span>What the patient sees</span></figcaption>
            <div className="hp-page" data-rx="true">
              <Rx org={org} />
              {spot}
            </div>
          </figure>
        </div>
      ) : (
        <div className="hp-page">
          <img key={step.img} src={`/demo/hospital/${step.img}.jpg`} alt={`Discharge summary, page ${step.img.slice(2)} (identities removed)`} width="760" height="961" />
          {spot}
        </div>
      )}
    </div>
  );

  // ProductLayout drops the tab bar for a one-step document, so the
  // prescription's single step keeps its tab (and its Trapezium ID) here, as a
  // compact selected pill in the same place.
  const oneStep = d.steps.length === 1 ? (
    <div className="hp-one-step">
      <StepTabs
        steps={d.steps.map((s) => s.nav)}
        active={0}
        onChange={setActive}
        idBase={`product-${SCREEN}`}
        trapeziumId={(i) => SAI_IDS.step(SCREEN, d.steps[i].key)}
        label={`${d.name}: steps`}
      />
    </div>
  ) : null;

  const above = (
    <>
      <div className="hp-docs" role="group" aria-label="Document">
        {DOC_ORDER.map((k) => (
          <button
            key={k}
            ref={(el) => { cards.current[k] = el; }}
            type="button"
            aria-pressed={doc === k}
            onClick={() => pickDoc(k)}
            onKeyDown={onDocKey}
            data-trapezium-id={IDS.doc(k)}
          >
            <b>{DOCS[k].name}</b><span>{DOCS[k].foot}</span>
          </button>
        ))}
      </div>
      {oneStep}
    </>
  );

  return (
    <ProductLayout
      screenKey={SCREEN}
      steps={d.steps}
      active={active}
      onStep={setActive}
      state={`${doc}-step-${d.steps[active].key}`}
      above={above}
      visual={visual}
      note={doc === "rx" ? "Smart Rx is a design proposal: there is no live prescription sample yet. Both pages are synthetic." : "A real discharge summary, with the hospital, patient and clinician identities removed."}
    />
  );
}
